import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle, Loader2 } from 'lucide-react';
import contactInfo from '../constants/contactInfo.json';

const ContactForm: React.FC = () => {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const contactEmail = contactInfo.email;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    // Simulate sending
    setTimeout(() => {
      setStatus('success');
      
      // Also trigger mailto as a fallback/real action if desired
      const mailtoLink = `mailto:${contactEmail}?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(`From: ${formData.name}\nEmail: ${formData.email}\n\n${formData.message}`)}`;
      window.location.href = mailtoLink;

      setTimeout(() => setStatus('idle'), 5000);
    }, 2000);
  };

  return (
    <div className="contact-form-container">
      <AnimatePresence mode="wait">
        {status === 'success' ? (
          <motion.div 
            key="success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="success-message"
            style={{ textAlign: 'center', padding: '40px' }}
          >
            <motion.div
              initial={{ rotate: -90, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: 'spring', damping: 10 }}
            >
              <CheckCircle size={80} className="text-gradient" style={{ margin: '0 auto 20px' }} />
            </motion.div>
            <h2>Message Sent!</h2>
            <p style={{ color: 'var(--text-secondary)' }}>We'll get back to you at {formData.email} soon.</p>
          </motion.div>
        ) : (
          <motion.form 
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onSubmit={handleSubmit}
            className="futuristic-form"
          >
            <div className="input-group">
              <input 
                type="text" 
                name="name" 
                placeholder="Full Name" 
                required 
                value={formData.name}
                onChange={handleChange}
                disabled={status === 'sending'}
              />
              <div className="input-focus-line"></div>
            </div>

            <div className="input-group">
              <input 
                type="email" 
                name="email" 
                placeholder="Email Address" 
                required 
                value={formData.email}
                onChange={handleChange}
                disabled={status === 'sending'}
              />
              <div className="input-focus-line"></div>
            </div>

            <div className="input-group">
              <input 
                type="text" 
                name="subject" 
                placeholder="Subject" 
                required 
                value={formData.subject}
                onChange={handleChange}
                disabled={status === 'sending'}
              />
              <div className="input-focus-line"></div>
            </div>

            <div className="input-group">
              <textarea 
                name="message" 
                placeholder="How can we help you?" 
                rows={5} 
                required 
                value={formData.message}
                onChange={handleChange}
                disabled={status === 'sending'}
              ></textarea>
              <div className="input-focus-line"></div>
            </div>

            <motion.button 
              type="submit" 
              className="primary-btn large"
              disabled={status === 'sending'}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
            >
              {status === 'sending' ? (
                <>
                  <Loader2 className="animate-spin" /> Sending to {contactEmail}...
                </>
              ) : (
                <>
                  <Send size={18} /> Send Message
                </>
              )}
            </motion.button>
          </motion.form>
        )}
      </AnimatePresence>

      <style>{`
        .futuristic-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .input-group {
          position: relative;
        }

        .input-group input, 
        .input-group textarea {
          width: 100%;
          padding: 15px;
          background: var(--bg-glass);
          border: 1px solid var(--border-subtle);
          border-radius: 10px;
          color: var(--text-main);
          font-family: inherit;
          font-size: 14px;
          outline: none;
          transition: all 0.3s ease;
        }

        .input-group input:focus, 
        .input-group textarea:focus {
          background: rgba(255, 255, 255, 0.08);
          border-color: var(--accent);
          box-shadow: 0 0 15px rgba(99, 102, 241, 0.2);
        }

        .input-focus-line {
          position: absolute;
          bottom: 0;
          left: 50%;
          width: 0;
          height: 2px;
          background: var(--accent);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          transform: translateX(-50%);
        }

        .input-group input:focus ~ .input-focus-line,
        .input-group textarea:focus ~ .input-focus-line {
          width: 100%;
        }

        .animate-spin {
          animation: rotate 1s linear infinite;
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export { ContactForm };
